import { SEARCH_APPEARS_ON_FILTER_FIELD } from '@disco/constants/search'

import { FilterOperator, FilterState } from 'types/filter-page'

/**
 * Validates if a filter value can be committed based on its type
 * @param filterType The type of filter (e.g., 'date', 'text', etc.)
 * @param value The filter value
 * @param operator The filter operator
 * @returns true if the value is valid and can be committed
 */
export const canCommitFilterValue = (
  filterType: string | undefined,
  value: any,
  operator?: FilterOperator,
): boolean => {
  switch (filterType) {
    case 'date':
    case 'partial_date':
    case 'number':
    case 'year':
      return validateBetweenFilterValues(value, operator)

    default:
      // Most filter types don't need validation
      return true
  }
}

// Accepts a plain string so callers with UI-only radio keys (not just
// FilterOperator) can format their labels; unknown keys pass through unchanged.
export const formatComparisonOperator = (operator?: string) => {
  switch (operator) {
    case 'equals':
      return 'is'
    case 'contains':
      return 'contains'
    case 'between':
      return 'is between'
    case 'lt':
      return 'is less than'
    case 'lte':
      return 'is before or equal to'
    case 'gt':
      return 'is more than'
    case 'gte':
      return 'is after or equal to'
    case 'not_equals':
      return 'is not'
    default:
      return operator
  }
}

/** Value the "No playlists" (appears-on count is zero) option commits. */
export const NO_PLAYLISTS_FILTER_VALUE = '0'
export const NO_PLAYLISTS_BADGE_LABEL = 'No playlists'

/**
 * The "Appears on" filter renders a "No playlists" shortcut for `equals 0`.
 */
export const isNoPlaylistsFilter = (state: FilterState): boolean =>
  state.definition.field === SEARCH_APPEARS_ON_FILTER_FIELD &&
  state.operator === 'equals' &&
  state.value === NO_PLAYLISTS_FILTER_VALUE

export const formatBadgeLabel = (state: FilterState): string => {
  if (isNoPlaylistsFilter(state)) {
    return NO_PLAYLISTS_BADGE_LABEL
  }

  if (!state.value) {
    return ''
  }

  // Check if this is an "all" selection
  if (Array.isArray(state.value) && state.value.length > 0) {
    const firstGroup = state.value[0]
    if (firstGroup && firstGroup._isAllSelection) {
      return 'All'
    }
  }

  if (state.definition.type === 'tags' || state.definition.type === 'group')
    return state.value.length

  return `${formatComparisonOperator(state.operator)} ${formatFilterValue(
    state.operator,
    state.value,
  )}`
}

export const formatFilterValue = (
  operator: FilterOperator | undefined,
  value: any,
): string => {
  // Handle all array values
  if (Array.isArray(value)) {
    if (operator === 'between' && value.length === 2) {
      return `${value[0]} - ${value[1]}`
    }
    // For other array values (like dropdown filters), join with comma and space
    return value.join(', ')
  }

  return value
}

export const getBetweenRangeErrors = (value: any) => {
  const startMissing = Array.isArray(value) && !value[0] && !!value[1]
  const endMissing = Array.isArray(value) && !!value[0] && !value[1]
  return { startMissing, endMissing, isIncomplete: startMissing || endMissing }
}

// A 'between' range is committable unless exactly one bound is filled; both
// empty is allowed so the filter can be cleared. Other operators always pass.
const validateBetweenFilterValues = (
  value: any,
  operator?: FilterOperator,
): boolean =>
  operator !== 'between' || !getBetweenRangeErrors(value).isIncomplete
